import { useIdentity } from '@vp/auth-react';
import { useContext, useState } from 'react';
import { IIdentityObject, ISummaryResponse } from '../interfaces/app-interfaces';
import { getChatbotToken, getRestId } from '../utils/session-storage-utils';
import { API_ENDPOINTS } from '../config/api-config';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { useNRLogging } from './custom-hooks/useNRLogging';
import { CAContext } from '../contexts/CAContext';

export const useGenerateAcw = () => {
  const { logCustomAttributesNR } = useNRLogging();
  const { authorizationHeader = '', canonicalId = '' } = useIdentity() as IIdentityObject;
  const {
    state: { sessionId },
  } = useContext(ChatbotContext);
  const {
    chatState: { chatSession },
  } = useContext(CAContext);

  const [summary, setSummary] = useState<ISummaryResponse | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const summaryAPI = async (): Promise<ISummaryResponse | undefined> => {
    if (chatSession !== 'chatbotSession') {
      console.log('summaryAPI called for non-chatbot session');
      return;
    }
    const payload = {
      order_number: '',
      shopper_id: canonicalId,
      session_id: sessionId || '',
      request_id: getRestId() || '',
    };

    const chatbotToken = getChatbotToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: authorizationHeader,
      'chatbot-authorization': `Bearer ${chatbotToken}`,
    };

    setLoading(true);
    setError(undefined);

    try {
      logCustomAttributesNR('ca-chatbot-api-request', {
        logLevel: 'info',
        message: 'Starting summary API request',
        apiType: 'summaryAPI',
      });
      const response = await fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/summarize-chat-v2`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.statusText}`);
      }

      const data: ISummaryResponse = await response.json();
      setSummary(data);
      logCustomAttributesNR('ca-chatbot-api-response', {
        logLevel: 'info',
        message: 'Summary API request successful',
        apiType: 'summaryAPI',
      });
      return data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      console.error('Error in summaryAPI:', message);
      logCustomAttributesNR('ca-chatbot-api-response', {
        logLevel: 'error',
        message: message,
        apiType: 'summaryAPI',
      });
    } finally {
      setLoading(false);
    }
  };

  return { summaryAPI, data: summary, error, loading };
};
